import React, { useEffect, useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import ClientPortal from '@/components/client-portal'
import AiSearchField from './ai-search-field'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import SimpleTooltip from '@/components/ui/simple-tooltip'
import { useSearch } from '@/components/search-provider'
import AuraCard from '../aura-card'

import AiIcon from '@/icons/ai-icon.svg'
import RefreshIcon from '@/icons/bwi/refresh.svg'
import CloseIcon from '@/icons/bwi/close.svg'

import { cx } from '@/utils/strings'
import { useLocation } from '@/utils/url'
import { removeLocalePrefix, useTranslations } from '@/utils/locale'
import useScrollLock from '@/utils/useScrollLock'
import { getModKey } from '@/utils/user-info'
import { isPrint } from '@/utils/print'
import { isAlgoliaAiConfigValid } from './api'
import useAskAiSettings from './use-ask-ai-settings'

const { VITE_ALGOLIA_AI_AGENT_ID, VITE_ALGOLIA_AI_AGENT_ID_SALES } = import.meta.env

const IconButton = ({
  className,
  icon,
  onClick,
  label,
}: {
  className?: string
  icon: React.ReactNode
  onClick: () => void
  /** Required accessible name for the button (icon-only buttons have no text). */
  label: string
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={cx(
      'text-gray-500 hover:text-gray-700 bg-white z-10 relative rounded-full border border-gray-300 aspect-square size-7.5 flex items-center justify-center',
      className
    )}
    onClick={onClick}
  >
    {icon}
  </button>
)

type FloatingChatProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function FloatingChat({ isOpen, setIsOpen }: FloatingChatProps) {
  const { t } = useTranslations()
  const { path } = useLocation()
  const pathWithoutLocale = removeLocalePrefix(path)
  const { enabled: askAiEnabled, promptSuggestions: suggestions = [] } = useAskAiSettings() || {}

  const {
    setAiQuery,
    isAiLoading,
    setIsAiLoading,
    isChatFullscreen,
    setIsChatFullscreen: setIsFullscreen,
    isFloatingChatEnabled,
    isNavDrawerOpen,
    askAiAgent,
    trackChatOpen,
  } = useSearch()

  const [showSuggestions, setShowSuggestions] = useState(true)
  const [renderKey, setRenderKey] = useState(0)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const isFullscreen = isChatFullscreen || !isDesktop

  // Handle manual chat open
  const handleChatOpen = () => {
    setIsOpen(true)
    trackChatOpen('button', { chatSource: 'manual', pagePath: pathWithoutLocale })
  }

  const handleChatClose = () => {
    setIsOpen(false)
    setIsFullscreen(false)
  }

  const { lock, unlock } = useScrollLock()
  useEffect(() => {
    if (isOpen && isFullscreen) {
      lock()
    } else {
      unlock()
    }
    return () => {
      unlock()
    }
  }, [isOpen, isFullscreen, lock, unlock])

  useEffect(() => {
    if (isAiLoading) {
      setShowSuggestions(false)
    }
  }, [isAiLoading])

  const prevAskAiAgentRef = React.useRef(askAiAgent)
  useEffect(() => {
    // If sales context changes while chat is open, close the chat to prevent issues with agent configuration
    if (prevAskAiAgentRef.current !== askAiAgent) {
      handleChatClose()
      setRenderKey((prev) => prev + 1) // Reset chat state
      prevAskAiAgentRef.current = askAiAgent
    }
  }, [askAiAgent])

  if (askAiEnabled === false || !isAlgoliaAiConfigValid() || !isFloatingChatEnabled || isPrint()) {
    return null
  }

  return (
    <ClientPortal>
      <div className="fixed bottom-4 right-4 z-50">
        {!isOpen && !isNavDrawerOpen && (
          <div className="relative">
            <button
              type="button"
              onClick={handleChatOpen}
              aria-label={`${t('Ask AI')} (${getModKey()} + I)`}
              title={`${t('Ask AI')} (${getModKey()} + I)`}
            >
              <AuraCard
                className={cx(
                  'group flex items-center justify-center shadow-2xl rounded-full pl-2.5 pr-4 py-2',
                  'bg-white dark:bg-secondary-900',
                  // hover effects
                  'before:transition-opacity after:transition-opacity before:duration-300 after:duration-300',
                  'before:opacity-50 after:opacity-50 hover:before:opacity-100 hover:after:opacity-100'
                )}
                colors={['var(--color-bw-teal-highlight)', 'var(--color-primary-500)']}
                speed="3s"
                glowSize="sm"
              >
                <AiIcon aria-hidden="true" className={cx('size-5 relative z-10')} />
                <span className="ml-1 text-sm font-medium text-secondary-800 dark:text-white">{t('Chat Now')}</span>
              </AuraCard>
            </button>
          </div>
        )}
      </div>
      {/* Fullscreen Scrim */}
      <div
        className={cx(
          'fixed inset-0 z-50 origin-top scale-y-150 transition-opacity duration-500 overflow-hidden',
          'grid grid-cols-1 bg-gradient-to-b backdrop-blur-3xl',
          'from-secondary-800/0 to-secondary-800 dark:from-gray-800/0 dark:to-gray-800',
          isFullscreen && isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={handleChatClose}
      ></div>
      <div
        className={cx(
          'transition-transform fixed bottom-4 right-4 z-50 max-w-full',
          isOpen ? 'translate-y-0' : 'translate-y-[calc(100%+1rem)] pointer-events-none',
          isFullscreen
            ? 'px-2 md:px-4 md:py-4 bottom-0 left-0 right-0 w-full'
            : 'px-0.5 pt-1.5 pb-0.5 bottom-4 right-4 w-105 bg-secondary-50/90 dark:bg-secondary-900/90 backdrop-blur-3xl rounded-2xl shadow-2xl border border-black/20 dark:border-white/10'
        )}
      >
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex gap-2 pt-1.5 px-2.5">
            <div className="flex-1 flex flex-col gap-2 justify-center">
              {showSuggestions && (
                <>
                  <span
                    className={cx(
                      'font-medium pt-1 pl-1',
                      isFullscreen
                        ? 'text-lg text-secondary-900/95 dark:text-white'
                        : 'text-sm text-secondary-900 dark:text-white',
                      isOpen ? 'opacity-100 delay-200' : 'opacity-0',
                      'transition-opacity duration-200 ease-anticipate'
                    )}
                  >
                    {t('Hi there! What questions do you have?')}
                  </span>
                  {(askAiAgent === 'help' || suggestions.length > 0) && (
                    <StaggerList
                      key={path}
                      className={cx('list-none flex flex-col gap-0.5 mb-1', isFullscreen ? 'pl-4' : 'pl-2.5')}
                      isVisible={isOpen}
                      reverseDirection
                    >
                      {suggestions.map((suggestion) => (
                        <StaggerListItem key={suggestion.button}>
                          <CtaLink
                            asButton
                            style={buttonStyles.btnText}
                            className={cx(
                              isFullscreen ? 'text-base text-secondary-900/95' : 'text-sm text-secondary-900',
                              'dark:text-white hover:text-primaryBlue dark:hover:text-primary-300'
                            )}
                            display="Inline"
                            onClick={() => {
                              setAiQuery(suggestion.prompt)
                              setShowSuggestions(false)
                              // track with plausible
                              if (typeof window.plausible === 'function') {
                                window.plausible('AI Chat Starter Suggestion Clicked', {
                                  props: {
                                    agent: askAiAgent ?? '',
                                    pagePath: path,
                                    ui: 'button',
                                    suggestion: suggestion.prompt,
                                  },
                                })
                              }
                            }}
                          >
                            {suggestion.button}
                          </CtaLink>
                        </StaggerListItem>
                      ))}
                      {suggestions.length === 0 && (
                        <StaggerListItem>
                          <CtaLink
                            asButton
                            style={buttonStyles.btnText}
                            className={cx(
                              isFullscreen
                                ? 'text-base text-white hover:text-primary-300 dark:hover:text-primary-300'
                                : 'text-sm hover:text-primaryBlue dark:hover:text-primary-300'
                            )}
                            display="Inline"
                            onClick={() => {
                              setAiQuery(t('Give a short, concise summary of the content on this page'))
                              setShowSuggestions(false)
                            }}
                          >
                            {t('Summarize this page')}
                          </CtaLink>
                        </StaggerListItem>
                      )}
                    </StaggerList>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-1">
              <IconButton
                className="hidden md:inline-flex"
                label={isFullscreen ? t('Exit Fullscreen') : t('Fullscreen')}
                icon={
                  isFullscreen ? (
                    <svg className="size-4" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                      />
                    </svg>
                  ) : (
                    <svg className="size-4" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                      />
                    </svg>
                  )
                }
                onClick={() => {
                  setIsFullscreen((prev) => !prev)
                }}
              />
              {!showSuggestions && (
                <SimpleTooltip backgroundColor="Black" content={t('Restart')}>
                  <IconButton
                    label={t('Restart')}
                    icon={<RefreshIcon className="size-4.5" aria-hidden="true" />}
                    onClick={() => {
                      setRenderKey((prev) => prev + 1) // Force remount to reset internal state
                      setShowSuggestions(true)
                      setIsAiLoading(false)
                    }}
                  />
                </SimpleTooltip>
              )}
              <IconButton
                label={t('Close')}
                icon={<CloseIcon className="size-4.5" aria-hidden="true" />}
                onClick={handleChatClose}
              />
            </div>
          </div>

          <AiSearchField
            key={renderKey}
            size={isFullscreen ? 'md' : 'sm'}
            className="max-h-[calc(100dvh-4.25rem)] bg-transparent! rounded-2xl!"
            agentId={
              askAiAgent === 'sales'
                ? VITE_ALGOLIA_AI_AGENT_ID_SALES || VITE_ALGOLIA_AI_AGENT_ID
                : VITE_ALGOLIA_AI_AGENT_ID
            }
            trackingMetadata={{ ui: 'button' }}
          />
        </div>
      </div>
    </ClientPortal>
  )
}
